import React from "react";
import {
  Eye,
  Newspaper,
  Briefcase,
  SlidersHorizontal,
  LineChart,
  Radar,
  Activity,
  Trophy,
  Cpu,
  Layers,
  FolderTree,
  BellRing,
  FileText,
  ShieldCheck,
  X,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [
      { id: "dashboard", label: "Watchlist", icon: Eye },
      { id: "marketnews", label: "Market News", icon: Newspaper },
      { id: "portfolio", label: "Portfolio", icon: Briefcase },
      { id: "monitor", label: "Monitor", icon: BellRing },
      { id: "reports", label: "Reports", icon: FileText },
    ],
  },
  {
    label: "Research",
    items: [
      { id: "screener", label: "Screener", icon: SlidersHorizontal },
      { id: "chart", label: "Chart", icon: LineChart },
      { id: "globalscan", label: "Global Scan", icon: Radar },
      { id: "movers", label: "Daily Movers", icon: Activity },
    ],
  },
  {
    label: "Stock Lists",
    items: [
      { id: "topstocks", label: "Top Stocks", icon: Trophy },
      { id: "chips", label: "Chip Stocks", icon: Cpu },
      { id: "etfs", label: "ETFs & Index Funds", icon: Layers },
      { id: "sectors", label: "Sectors", icon: FolderTree },
    ],
  },
];

function NavButton({ id, label, icon: Icon, isActive, onClick }) {
  return (
    <button
      onClick={() => onClick(id)}
      className="relative w-full flex items-center gap-2.5 pl-3.5 pr-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 text-left group"
      style={{
        backgroundColor: isActive ? "rgba(67, 97, 238, 0.12)" : "transparent",
        color: isActive ? "#c3cdfb" : "var(--text-secondary)",
      }}
      onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "var(--bg-card)"; e.currentTarget.style.color = "var(--text-primary)"; } }}
      onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--text-secondary)"; } }}
    >
      {isActive && (
        <span
          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full"
          style={{ background: "linear-gradient(180deg, var(--accent-blue), var(--link-blue))" }}
        />
      )}
      {Icon && <Icon size={16} strokeWidth={2} style={{ color: isActive ? "var(--link-blue)" : "var(--text-muted)", flexShrink: 0 }} />}
      <span className="truncate">{label}</span>
    </button>
  );
}

export default function Sidebar({ activePage, setActivePage, open, onClose, isAdmin }) {
  return (
    <aside
      className={`flex flex-col w-64 md:w-56 shrink-0 h-full fixed md:relative inset-y-0 left-0 z-40 transform transition-transform duration-200 md:translate-x-0 ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
      style={{ backgroundColor: "var(--bg-shell)", borderRight: "1px solid var(--border-color)" }}
    >
      {/* Logo */}
      <div className="px-5 py-5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border-color)" }}>
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm shrink-0"
            style={{
              background: "linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dark))",
              boxShadow: "0 4px 14px -4px rgba(67, 97, 238, 0.6)",
            }}
          >
            ST
          </div>
          <div>
            <div className="font-bold text-sm tracking-tight" style={{ color: "var(--text-primary)" }}>SwingTrader</div>
            <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#d9a63f" }}>Pro</div>
          </div>
        </div>
        <button onClick={onClose} className="md:hidden" aria-label="Close menu" style={{ color: "var(--text-muted)" }}>
          <X size={20} />
        </button>
      </div>

      {/* Scrollable nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5" style={{ scrollbarWidth: "thin", scrollbarColor: "var(--border-color) transparent" }}>
        {NAV_GROUPS.map((group, i) => (
          <div key={group.label} className={i > 0 ? "pt-4" : ""}>
            <div className="px-3.5 pb-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#4a5170" }}>
              {group.label}
            </div>
            {group.items.map((item) => (
              <NavButton key={item.id} {...item} isActive={activePage === item.id} onClick={setActivePage} />
            ))}
          </div>
        ))}

        {isAdmin && (
          <div className="pt-4">
            <div className="px-3.5 pb-1.5 text-[10px] font-bold uppercase tracking-widest" style={{ color: "#4a5170" }}>
              Admin
            </div>
            <NavButton id="admin" label="Admin Dashboard" icon={ShieldCheck} isActive={activePage === "admin"} onClick={setActivePage} />
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="px-5 py-3.5" style={{ borderTop: "1px solid var(--border-color)" }}>
        <div className="flex items-center gap-2 text-xs">
          <span className="relative flex h-1.5 w-1.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: "var(--gain-green)" }} />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5" style={{ backgroundColor: "var(--gain-green)" }} />
          </span>
          <div>
            <div className="font-medium" style={{ color: "var(--text-secondary)" }}>US Market</div>
            <div style={{ color: "#4a5170" }}>Swing trading signals &amp; analytics</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
